import { createWebHistory, createRouter } from "vue-router";
import axios from 'axios';
import routes from './routes';
import FullRoutes from './FullRoutes';
import appConfig from "../../app.config";
import store from '@/state/store';

axios.defaults.baseURL = process.env.VUE_APP_APIURL;
axios.defaults.withCredentials = true;
if (process.env.NODE_ENV !== "production") {
  axios.defaults.headers.common["X-Auth-Context"] = "cau";
}
let routeNames = new Set(routes.map(route => route.name));
const REFRESH_ENDPOINT = '/ad/refresh';
const LOGIN_PATH = '/login';
let refreshPromise = null;
let isRedirectingToLogin = false;

function redirectToLoginOnce() {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  localStorage.removeItem('userid');
  // Já estando em /login, atribuir a mesma URL a location.href força uma recarga
  // completa da página. Como a trava `isRedirectingToLogin` zera a cada carga,
  // qualquer 401 recorrente na própria tela de login viraria loop de reload.
  if (window.location.pathname === LOGIN_PATH) return;
  window.location.href = LOGIN_PATH;
}

function isRefreshRequest(config) {
  const requestUrl = config && config.url ? String(config.url) : '';
  return requestUrl.includes(REFRESH_ENDPOINT);
}

// O login LDAP (POST /ad) trata o próprio 401 no call site: o login.vue mostra
// a mensagem de erro ("Usuário ou senha incorretos"). Sem esta exceção o 401 do
// login cairia no redirect global abaixo (na tela de login não há `userid`),
// recarregando a página e apagando a mensagem. /ad/refresh já sai por
// isRefreshRequest.
function isLoginRequest(config) {
  const requestUrl = config && config.url ? String(config.url).split('?')[0] : '';
  return requestUrl.endsWith('/ad') || requestUrl === 'ad';
}

axios.interceptors.response.use(
  response => response,
  async error => {
    const status = error && error.response ? error.response.status : null;
    const originalRequest = error ? error.config : null;

    if (status !== 401 || !originalRequest) return Promise.reject(error);
    if (isRefreshRequest(originalRequest) || originalRequest._retry) return Promise.reject(error);
    if (isLoginRequest(originalRequest)) return Promise.reject(error);

    if (!localStorage.getItem('userid')) {
      redirectToLoginOnce();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios.post(REFRESH_ENDPOINT, {}, { withCredentials: true }).finally(() => {
          refreshPromise = null;
        });
      }

      await refreshPromise;
      return axios(originalRequest);
    } catch (refreshError) {
      console.error("Erro ao renovar token:", refreshError);
      redirectToLoginOnce();
      return Promise.reject(refreshError);
    }
  }
);

// Em seguida, filtre as 'FullRoutes' para remover quaisquer rotas cujos nomes já existem em 'routes'
let uniqueFullRoutes = FullRoutes.filter(route => !routeNames.has(route.name));

// Agora você pode unir 'routes' e 'uniqueFullRoutes'
let combinedRoutes = routes.concat(uniqueFullRoutes);

const router = createRouter({
  history: createWebHistory(),
  routes:combinedRoutes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  
  if (!authRequired) return next();
  axios.defaults.withCredentials = true;
  //axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt'); // for all requests
  next();
});

router.beforeEach((routeTo, routeFrom, next) => {
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {

    // Check if auth is required on this route
    // (including nested routes).
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

    // If auth isn't required for the route, just continue.
    if (!authRequired) return next();

    // If auth is required and the user is logged in...
    if (store.getters['auth/loggedIn']) {
      // Validate the local user token...
      return store.dispatch('auth/validate').then((validUser) => {
        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        validUser ? next() : redirectToLogin();
      });
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin();

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }
  } else {
    const publicPages = ['/login', '/register', '/forgot-password'];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = localStorage.getItem('userid');

    if (authpage && !loggeduser) {
      return next('/login');
    }

    next();
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
